<template>
  <!--
    The IMPROVE Live webinar booking popup (Muhammad, 6 Oct 2026). Opened by
    the trial pill's "IMPROVE Live" button, the trial emails' link and the old
    /improve-live address; mounted once, in App.vue. A month calendar marks the
    upcoming webinars (Tuesdays and Thursdays); a booked company sees its date
    with Join, Add to calendar, Change date and Cancel. State: utils/webinarBooking.js.
  -->
  <v-dialog
    v-if="eligible"
    :value="webinar.open"
    max-width="780"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    scrollable
    @input="onInput"
  >
    <v-card class="wb">
      <div class="wb__head">
        <div>
          <p class="wb__eyebrow">IMPROVE Live webinar</p>
          <h2 class="wb__title">{{ heading }}</h2>
          <p class="wb__lead">
            A {{ webinar.durationMinutes }} minute live demonstration of IMPROVE, with time for your
            questions, on Microsoft Teams.<span v-if="scheduleText"> {{ scheduleText }}</span>
          </p>
        </div>
        <v-btn icon class="wb__close" aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="wb__body">
        <div v-if="!webinar.loaded" class="wb__centre">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <!-- Booked -->
        <div v-else-if="booking && !changing" class="wb__booked">
          <div class="wb__when">
            <v-icon size="34" color="#2fa84f">mdi-calendar-check</v-icon>
            <div>
              <p class="wb__when-date">{{ longDate(booking.date) }}</p>
              <p class="wb__when-time">{{ booking.time }} to {{ endTime(booking.time) }} UK time</p>
            </div>
          </div>
          <p class="wb__note">We have emailed you the calendar invite and the Teams link.</p>
          <div class="wb__actions">
            <v-btn color="primary" depressed class="wb__btn" :href="booking.join_url" target="_blank" rel="noopener">
              <v-icon left small>mdi-video</v-icon>Join on Teams
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined color="directHeader" class="wb__btn" v-bind="attrs" v-on="on">
                  <v-icon left small>mdi-calendar-plus</v-icon>Add to calendar
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item :href="outlookLink" target="_blank" rel="noopener">
                  <v-list-item-title>Outlook</v-list-item-title>
                </v-list-item>
                <v-list-item :href="googleLink" target="_blank" rel="noopener">
                  <v-list-item-title>Google Calendar</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadIcs">
                  <v-list-item-title>Apple or another calendar (.ics file)</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div class="wb__links">
            <v-btn text small color="directHeader" class="wb__link" @click="startChange">Change date</v-btn>
            <v-btn text small color="error" class="wb__link" @click="confirmingCancel = true">Cancel booking</v-btn>
          </div>
          <div v-if="confirmingCancel" class="wb__confirm">
            <p>Cancel your place on {{ booking.short_label }}?</p>
            <v-btn small depressed color="error" class="wb__btn" :loading="saving" @click="cancel">Yes, cancel it</v-btn>
            <v-btn small text class="wb__btn" @click="confirmingCancel = false">Keep my place</v-btn>
          </div>
        </div>

        <!-- Choose a date -->
        <div v-else-if="webinar.sessions.length" class="wb__pick">
          <v-date-picker
            v-model="date"
            class="wb__calendar"
            no-title
            :full-width="$vuetify.breakpoint.xsOnly"
            first-day-of-week="1"
            locale="en-gb"
            color="#002458"
            :min="minDate"
            :max="maxDate"
            :picker-date.sync="month"
            :allowed-dates="isOpenDate"
            :events="eventDates"
            :event-color="eventColor"
          />
          <div class="wb__slot">
            <template v-if="selectedSessions.length">
              <p class="wb__slot-date">{{ longDate(date) }}</p>
              <div v-for="s in selectedSessions" :key="s.session_at" class="wb__slot-row">
                <p class="wb__slot-time">{{ s.time }} to {{ endTime(s.time) }} UK time</p>
                <p class="wb__slot-places" :class="{ 'is-low': s.seats_remaining <= 3 }">{{ placesText(s) }}</p>
                <v-btn
                  color="primary"
                  depressed
                  block
                  class="wb__btn"
                  :loading="saving"
                  :disabled="s.is_full || isBookedSession(s)"
                  @click="book(s)"
                >
                  {{ changing ? "Move my booking here" : "Book this webinar" }}
                </v-btn>
              </div>
            </template>
            <p v-else class="wb__hint">Choose one of the highlighted dates.</p>
            <v-btn v-if="changing && booking" text block class="wb__btn mt-2" @click="changing = false">
              Keep {{ booking.short_label }}
            </v-btn>
            <div class="wb__legend">
              <span><i class="wb__dot wb__dot--open"></i>Places left</span>
              <span><i class="wb__dot wb__dot--full"></i>Full</span>
              <span v-if="changing && booking"><i class="wb__dot wb__dot--mine"></i>Your booking</span>
            </div>
          </div>
        </div>

        <div v-else class="wb__centre">
          <p class="wb__note">There are no webinar dates to book right now. Please check back soon.</p>
        </div>

        <v-alert v-if="error" type="error" text dense class="wb__error">{{ error }}</v-alert>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import {
  webinar,
  bookWebinar,
  canBookWebinar,
  cancelWebinarBooking,
  closeWebinarBooking,
  currentBooking,
  loadWebinar
} from "@/utils/webinarBooking";
import { downloadIcsFile, googleCalendarLink, outlookCalendarLink } from "@/utils/calendarLinks";

const COLOUR_OPEN = "#2fa84f";
const COLOUR_FULL = "#b0b8c4";
const COLOUR_MINE = "#002458";

export default {
  name: "WebinarBookingDialog",
  data() {
    return {
      webinar: webinar,
      date: null,
      month: null,
      changing: false,
      saving: false,
      confirmingCancel: false,
      error: null
    };
  },
  computed: {
    eligible() {
      return canBookWebinar(this.$store.getters);
    },
    // The signed in account: a different one reloads the webinar data.
    account() {
      const user = this.$store.getters.user || {};
      return this.eligible && user.id ? String(user.company_id || "") + ":" + String(user.id) : null;
    },
    booking() {
      return currentBooking();
    },
    heading() {
      if (this.booking && !this.changing) return "Your webinar is booked";
      return this.changing ? "Choose a new date" : "Book a webinar";
    },
    sessionsByDate() {
      const byDate = {};
      this.webinar.sessions.forEach(function(s) {
        (byDate[s.date] = byDate[s.date] || []).push(s);
      });
      return byDate;
    },
    eventDates() {
      return Object.keys(this.sessionsByDate);
    },
    minDate() {
      return this.eventDates.length ? this.eventDates[0] : undefined;
    },
    maxDate() {
      return this.eventDates.length ? this.eventDates[this.eventDates.length - 1] : undefined;
    },
    selectedSessions() {
      return this.date ? this.sessionsByDate[this.date] || [] : [];
    },
    // "Tuesdays at 11:00 and Thursdays at 14:00 (UK time)", from the dates offered.
    scheduleText() {
      const seen = {};
      const parts = [];
      this.webinar.sessions.forEach(function(s) {
        const key = moment(s.date).format("dddd") + "s at " + s.time;
        if (!seen[key]) {
          seen[key] = true;
          parts.push(key);
        }
      });
      return parts.length ? parts.join(" and ") + " (UK time)." : "";
    },
    calendarEvent() {
      const b = this.booking;
      if (!b) return null;
      return {
        uid: b.calendar.uid,
        sequence: b.calendar.sequence,
        title: b.calendar.title,
        details: b.calendar.details,
        start: b.session_at,
        end: b.ends_at,
        url: b.join_url
      };
    },
    outlookLink() {
      return this.calendarEvent ? outlookCalendarLink(this.calendarEvent) : "#";
    },
    googleLink() {
      return this.calendarEvent ? googleCalendarLink(this.calendarEvent) : "#";
    }
  },
  watch: {
    account: {
      immediate: true,
      handler(account) {
        if (account) loadWebinar(account);
      }
    },
    // Immediate: a link (the trial emails' next=improve-live) can open the
    // popup before this component exists, while the app is signing in.
    "webinar.open": {
      immediate: true,
      handler(open) {
        if (!open) return;
        this.changing = false;
        this.confirmingCancel = false;
        this.error = null;
        this.date = null;
        // Places change all the time: refresh them every time the popup opens.
        loadWebinar(this.account, true).then(this.showFirstMonth);
      }
    }
  },
  // Signing out removes this component (App.vue): close the popup with it, so
  // the next sign in in this tab does not find it open.
  beforeDestroy() {
    closeWebinarBooking();
  },
  methods: {
    onInput(open) {
      if (!open) this.close();
    },
    close() {
      closeWebinarBooking();
    },
    showFirstMonth() {
      if (this.minDate) this.month = this.minDate.slice(0, 7);
    },
    isOpenDate(date) {
      return (this.sessionsByDate[date] || []).some(s => !s.is_full && !this.isBookedSession(s));
    },
    eventColor(date) {
      const sessions = this.sessionsByDate[date] || [];
      if (this.changing && sessions.some(this.isBookedSession)) return COLOUR_MINE;
      return sessions.every(s => s.is_full) ? COLOUR_FULL : COLOUR_OPEN;
    },
    isBookedSession(s) {
      return !!this.booking && this.booking.session_at === s.session_at;
    },
    longDate(date) {
      return moment(date).format("dddd D MMMM YYYY");
    },
    endTime(time) {
      return moment(time, "HH:mm").add(this.webinar.durationMinutes, "minutes").format("HH:mm");
    },
    placesText(s) {
      if (s.is_full) return "Fully booked";
      return s.seats_remaining + (s.seats_remaining === 1 ? " place left" : " places left");
    },
    startChange() {
      this.changing = true;
      this.confirmingCancel = false;
      this.error = null;
      this.date = null;
      this.showFirstMonth();
    },
    book(session) {
      const moving = this.changing;
      this.saving = true;
      this.error = null;
      bookWebinar(session.session_at)
        .then(booking => {
          this.changing = false;
          this.date = null;
          if (booking) {
            this.$snotify.success(
              moving
                ? "Moved to " + booking.short_label + ". Your calendar invite is updated."
                : "You are booked for " + booking.short_label + ". We have emailed your calendar invite."
            );
          }
        })
        .catch(err => {
          this.error = this.messageFrom(err);
          loadWebinar(this.account, true);
        })
        .then(() => {
          this.saving = false;
        });
    },
    cancel() {
      this.saving = true;
      this.error = null;
      cancelWebinarBooking()
        .then(() => {
          this.confirmingCancel = false;
          this.$snotify.success("Your webinar booking is cancelled.");
          this.showFirstMonth();
        })
        .catch(err => {
          this.error = this.messageFrom(err);
          loadWebinar(this.account, true);
        })
        .then(() => {
          this.saving = false;
        });
    },
    downloadIcs() {
      if (this.calendarEvent) downloadIcsFile(this.calendarEvent, "improve-live-webinar.ics");
    },
    messageFrom(err) {
      const data = err && err.response && err.response.data;
      return (data && data.message) || "Something went wrong. Please try again.";
    }
  }
};
</script>

<style scoped>
.wb {
  border-radius: 14px !important;
  overflow: hidden;
}

.wb__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px 16px;
  background: linear-gradient(135deg, rgba(123, 200, 163, 0.22), rgba(0, 36, 88, 0.05)), #fff;
  border-bottom: 1px solid rgba(0, 36, 88, 0.08);
}

.wb__eyebrow {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #08a0a8;
}

.wb__title {
  margin: 0 0 6px;
  font-size: 22px;
  line-height: 1.25;
  font-weight: 700;
  color: #002458;
}

.wb__lead {
  margin: 0;
  max-width: 60ch;
  font-size: 14px;
  line-height: 1.5;
  color: #5a6a7a;
}

.wb__close {
  margin: -6px -8px 0 0;
}

.wb__body {
  padding: 20px 24px 24px !important;
}

.wb__centre {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  text-align: center;
}

.wb__pick {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: start;
}

.wb__calendar {
  box-shadow: none !important;
  border: 1px solid rgba(0, 36, 88, 0.1);
  border-radius: 10px !important;
}

.wb__slot {
  padding: 16px 18px;
  border-radius: 10px;
  background: #f6f8fb;
  border: 1px solid rgba(0, 36, 88, 0.06);
}

.wb__slot-date {
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 700;
  color: #002458;
}

.wb__slot-row + .wb__slot-row {
  margin-top: 14px;
}

.wb__slot-time {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 600;
  color: #24364d;
}

.wb__slot-places {
  margin: 0 0 12px;
  font-size: 13px;
  color: #2f7a52;
}

.wb__slot-places.is-low {
  color: #c8641a;
  font-weight: 600;
}

.wb__hint {
  margin: 0;
  font-size: 14px;
  color: #5a6a7a;
}

.wb__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 16px;
  font-size: 12px;
  color: #5a6a7a;
}

.wb__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-radius: 50%;
  vertical-align: 1px;
}

.wb__dot--open {
  background: #2fa84f;
}

.wb__dot--full {
  background: #b0b8c4;
}

.wb__dot--mine {
  background: #002458;
}

.wb__booked {
  max-width: 560px;
}

.wb__when {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 10px;
  background: #eef6f1;
  border: 1px solid rgba(123, 200, 163, 0.5);
}

.wb__when-date {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #002458;
}

.wb__when-time {
  margin: 2px 0 0;
  font-size: 14px;
  color: #3d5068;
}

.wb__note {
  margin: 14px 0 0;
  font-size: 14px;
  color: #5a6a7a;
}

.wb__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.wb__links {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 10px;
}

.wb__btn,
.wb__link {
  text-transform: none !important;
  letter-spacing: normal !important;
}

.wb__link {
  padding: 0 6px !important;
}

.wb__confirm {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fdf1f0;
  border: 1px solid rgba(179, 38, 30, 0.2);
}

.wb__confirm p {
  margin: 0 0 8px;
  font-size: 14px;
  color: #6b1d18;
}

.wb__error {
  margin: 16px 0 0 !important;
}

@media (max-width: 599px) {
  .wb__head {
    padding: 18px 16px 14px;
  }

  .wb__body {
    padding: 16px !important;
  }

  .wb__pick {
    grid-template-columns: 1fr;
  }
}
</style>
